/**
 * Lazy AWS S3 SDK loader.
 *
 * The AWS S3 SDK (~480KB) is only needed when a user uploads/lists/deletes
 * objects — never on initial load. Every call site imports from here and awaits
 * the loader instead of statically importing `@aws-sdk/*`, so webpack splits the
 * whole SDK into an on-demand chunk that's fetched on first upload.
 *
 * Import TYPES statically (erased at compile time, no runtime cost); import
 * VALUES only through the async loaders below.
 */
import type { S3Client as S3ClientType, S3ClientConfig } from '@aws-sdk/client-s3';
import type { Upload as UploadType } from '@aws-sdk/lib-storage';

/** The dynamically-imported @aws-sdk/client-s3 module (commands, S3Client, ...). */
export function loadS3ClientModule() {
  return import('@aws-sdk/client-s3');
}

/** The dynamically-imported @aws-sdk/lib-storage module (Upload). */
export function loadLibStorageModule() {
  return import('@aws-sdk/lib-storage');
}

/** The dynamically-imported @aws-sdk/credential-providers module. */
export function loadCredentialProviders() {
  return import('@aws-sdk/credential-providers');
}

/** The dynamically-imported @aws-sdk/s3-request-presigner module. */
export function loadS3Presigner() {
  return import('@aws-sdk/s3-request-presigner');
}

/**
 * Hard ceiling on any single S3 HTTP request, in ms.
 *
 * This is a deadlock backstop, NOT a performance SLA — it exists only so a
 * request can never hang forever, and should be comfortably longer than the
 * slowest legitimate part upload. The SDK default is 0, which means "wait
 * forever" (see createS3Client), so without this a stalled UploadPart leaves a
 * promise pending for the lifetime of the page.
 *
 * Sizing: a 5MB part on a badly degraded link (~180KB/s) needs ~28s of pure
 * transfer at full bandwidth. Concurrency divides that bandwidth, so the real
 * per-part wall time scales with how many parts are in flight — which is why
 * upload concurrency is capped adaptively at the call site. 10 minutes leaves
 * headroom for a slow-but-progressing part while still bounding a dead one.
 */
export const S3_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;

/** Bound on establishing the connection, as opposed to transferring the body. */
const S3_CONNECTION_TIMEOUT_MS = 30 * 1000;

/**
 * Run `promise` with a wall-clock bound, so a caller can never await forever.
 *
 * Defence in depth behind the SDK's own requestTimeout: this guards the whole
 * logical operation (including SDK-internal retries and credential refreshes),
 * not just one HTTP request. The rejection is a real Error, so it lands in the
 * caller's existing catch/abort/retry/telemetry path like any other failure —
 * which is the entire point. A silent hang bypasses all of that.
 *
 * Note this does not cancel the underlying work; it only stops us waiting on
 * it. The SDK request is separately bounded by S3_REQUEST_TIMEOUT_MS.
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`);
      err.name = 'TimeoutError';
      reject(err);
    }, timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * Build an S3Client, loading the SDK on demand.
 *
 * `requestHandler.requestTimeout` is load-bearing. The SDK's own default is
 * `requestTimeout(timeoutInMs = 0)`, whose body is `if (timeoutInMs) {...}` —
 * with 0 the promise never resolves AND never rejects, and it is consumed via
 * `Promise.race([fetch, requestTimeout(...)])`. Racing a never-settling promise
 * is the same as not racing at all, so a stalled request stayed pending
 * forever: no rejection meant the multipart catch block never ran, so
 * CompleteMultipartUpload never fired, AbortMultipartUpload never fired, the
 * retry loop never triggered and no failure telemetry was ever emitted. Every
 * orphaned multipart upload traces back to this one unset value.
 *
 * Passing a plain options object (rather than a handler instance) is
 * deliberate: runtimeConfig.browser does `FetchHttpHandler.create(requestHandler)`,
 * which constructs a handler from these options. That keeps @smithy/* out of
 * this module's imports so the lazy SDK chunk stays intact.
 *
 * `requestChecksumCalculation: 'WHEN_REQUIRED'` is load-bearing, not tuning.
 * Since SDK 3.729 the default is 'WHEN_SUPPORTED', which makes the flexible-
 * checksums middleware treat any non-string, non-ArrayBuffer body as a stream
 * (`isStreaming()`) and hand it to the browser build of
 * `getAwsChunkedEncodingStream`, which immediately calls `.getReader()` on it.
 * A Blob/File has no `getReader`, so every PutObject/UploadPart with a Blob
 * body died with "getReader is not a function" — that's Blob-bodied log
 * uploads and every multipart photo part. 'WHEN_REQUIRED' skips checksum
 * calculation for these operations and with it the chunked-encoding path.
 * S3 still gets Content-MD5/SigV4 integrity checking.
 */
export async function createS3Client(config: S3ClientConfig): Promise<S3ClientType> {
  const { S3Client } = await loadS3ClientModule();
  return new S3Client({
    requestChecksumCalculation: 'WHEN_REQUIRED',
    // 1, not 3, and deliberately so.
    //
    // The SDK's retries are invisible to us and multiply with our own: measured
    // in a real browser, one failing simple upload made 12 HTTP requests (3 SDK
    // x 4 ours) and one failing multipart made 9 per part (3 SDK x 3 per-part).
    // That is a lot of load generated on a service that is already unhappy, and
    // none of it is classified, backed off with jitter, or recorded — the SDK
    // just retries and eventually surfaces one error, so our telemetry sees a
    // single failure and cannot tell it took nine attempts to get there.
    //
    // Our loop is now the smarter of the two: it classifies before retrying,
    // gives up immediately on causes retrying cannot fix, backs off with jitter,
    // and records every attempt. So retry once here and let it decide.
    //
    // Load-bearing consequence: the SDK's retry layer also absorbed transient
    // 4xx conditions (clock skew, expired/rotating credentials). With this at 1
    // nothing else absorbs them, so classifyUploadError has to treat those codes
    // as retryable — see RECOVERABLE_AWS_CODES there. Changing this back to >1
    // without removing that would restore the multiplication.
    maxAttempts: 1,
    ...config,
    // After ...config so a caller can't accidentally drop the timeout by
    // passing its own requestHandler; merge instead of replace.
    requestHandler: {
      requestTimeout: S3_REQUEST_TIMEOUT_MS,
      connectionTimeout: S3_CONNECTION_TIMEOUT_MS,
      ...((config as any)?.requestHandler ?? {}),
    } as any,
  });
}

/**
 * Build a managed multipart Upload, loading @aws-sdk/lib-storage on demand.
 * `params` is the Upload constructor options ({ client, params: {...} }).
 */
export async function createUpload(params: ConstructorParameters<typeof UploadType>[0]): Promise<UploadType> {
  const { Upload } = await loadLibStorageModule();
  return new Upload(params);
}

/** Convenience: load Cognito-pool credentials provider and build creds. */
export async function createCognitoCredentials(
  args: Parameters<Awaited<ReturnType<typeof loadCredentialProviders>>['fromCognitoIdentityPool']>[0]
) {
  const { fromCognitoIdentityPool } = await loadCredentialProviders();
  return fromCognitoIdentityPool(args);
}
